<template>
  <ValidationObserver
    ref="conditionsObserver"
    slim
  >
    <AsidePanel
      :class="$style.AuthNPoliciesAsidePanel"
      :components="components"
      :disableCta="shouldDisableCta"
      :handleCancel="redirectToAuthNPolicies"
      :handleSave="handleSave"
      :isExpanded="isExpanded"
      :itemId="itemId"
      :itemType="$options.itemType"
      :noRedirect="noRedirect"
      redirectTo="/#/security/authnpolicies"
      :saveText="saveText"
      :viewport="viewport"
    >
      <template v-slot:avatar>
        <AsideIcon
          icon="conditionalPolicies"
        />
      </template>
      <template v-slot:status>
        <PolicyStatus />
      </template>
      <template
        v-if="isPlatformPlusFeature"
        v-slot:asideHeaderMessages
      >
        <PlatformPlusBadgeCard
          :class="$style.badge"
          :features="platformPlusFeatureList"
          :linkText="platformPlusLinkText"
          :message="platformPlusMessage"
          :pluralFeatureName="platformPlusFeatureName"
          :title="platformPlusTitle"
        />
      </template>
    </AsidePanel>
  </ValidationObserver>
</template>
<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { ValidationObserver } from 'vee-validate3';
import { authnPolicyType } from 'client/Security/AuthNPolicyType';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import AuthNPoliciesAsidePanelSections from 'client/Security/AuthNPoliciesAsidePanel/AuthNPoliciesAsidePanelSections.vue';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import MspState from 'client/components/models/MspState';
import PlatformPlusBadgeCard from 'client/components/PlatformPlusBadge/PlatformPlusBadgeCard.vue';
import PlatformPlusFeatureConstants from 'client/util/Constants/PlatformPlusFeatureConstants';
import PolicyStatus from 'client/Security/AuthNPoliciesAsidePanel/PolicyStatus.vue';
import Util from 'client/util/Util';

const { authNPolicies } = ItemTypeConsts;
const authNPolicyModel = StoreModelMap[authNPolicies];
const authNPolicyModule = StoreModuleMap[authNPolicies];
const platformPlusFeatureList = [
  PlatformPlusFeatureConstants.deviceTrust,
  PlatformPlusFeatureConstants.networkTrust,
];
const zeroTrustFeatureCode = trialFeatures.zeroTrust.featureCode;

export default {
  name: 'AuthNPoliciesAsidePanel',

  itemType: ItemTypeConsts.authNPolicies,

  components: {
    AsidePanel,
    AsideIcon,
    PolicyStatus,
    PlatformPlusBadgeCard,
    ValidationObserver,
  },

  mixins: [
    AsidePanelPropsMixin,
    FeatureTrialsMixin,
  ],

  data() {
    return {
      components: [{
        label: 'Details',
        vueComponent: AuthNPoliciesAsidePanelSections,
      }],
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information.',
      platformPlusFeatureName: 'Conditions',
      platformPlusFeatureList,
      platformPlusTitle: 'PREMIUM',
      platformPlusMessage: 'Adding conditions to an access policy is not part of your plan. You can upgrade your plan to include this feature.',
      platformPlusLinkText: 'Learn more',
    };
  },

  computed: {
    ...mapGetters(authNPolicyModule, ['getSelectedItemsCount']),

    ...mapGetters(authNPolicyModel, [
      'canSaveItem',
      'item',
      'hasItemChanged',
      'haveAssociationsChanged',
    ]),

    ...mapGetters('SettingsModule', [
      'hasBilling',
    ]),

    ...mapGetters('PlanModule', ['planHasFeature']),

    hasBillingRole() {
      return this.hasBilling
        && !MspState.isAnMspOrChild;
    },

    isPlatformPlusFeature() {
      return get(this.item, 'type') !== authnPolicyType.ldap && !this.planHasFeature(zeroTrustFeatureCode);
    },

    saveText() {
      return this.itemId ? 'update policy' : 'create policy';
    },

    shouldDisableCta() {
      return !this.hasItemChanged || !this.canSaveItem;
    },
  },
  created() {
    this.fetchEntitledFeatureCodes();
    this.fetchPlanInfo();
  },

  methods: {
    ...mapActions(authNPolicyModel, [
      'saveItem',
    ]),

    ...mapActions(authNPolicyModule, [
      'refreshCollection',
    ]),

    ...mapActions('AlertModule', [
      'triggerErrorAlert',
    ]),

    ...mapActions('PlanModule', ['fetchPlan']),

    async fetchPlanInfo() {
      if (this.hasBillingRole) await this.fetchPlan();
    },

    async handleSave() {
      const isFormValid = await this.$refs.conditionsObserver.validate();

      if (isFormValid) {
        const item = await this.saveItem();

        this.refreshCollection({
          item,
          forceRefresh: this.getSelectedItemsCount > 0,
        });
        this.refreshTrialData(zeroTrustFeatureCode);
      } else {
        throw new Error(this.validationErrorMessage);
      }
    },

    redirectToAuthNPolicies() {
      Util.redirect('/#/security/authnpolicies');
    },
  },
};
</script>

<style module>

.badge {
  margin-top: var(--jc-spacer);
}

.AuthNPoliciesAsidePanel {
  --headerTextTextOverflow: ellipsis;
  --headerTextWhiteSpace: nowrap;
}

</style>
